import { ReactNode, useEffect, useState } from 'react';
import { ArrowLeft, Check } from 'lucide-react';
import { SITE_NAME } from '@/lib/constants/branding';

interface OnboardingShellProps {
  children: ReactNode;
  step: number;
  totalSteps: number;
  stepLabel: string;
  onBack?: () => void;
  maxWidth?: string;
}

function StepProgressIndicator({ currentStep, totalSteps }: { currentStep: number; totalSteps: number }) {
  return (
    <div className="flex items-center w-full mb-8">
      {Array.from({ length: totalSteps }).map((_, index) => {
        const stepNumber = index + 1;
        const isActive = stepNumber === currentStep;
        const isCompleted = stepNumber < currentStep;
        const isLast = index === totalSteps - 1;

        return (
          <div key={index} className="flex items-center" style={{ flex: isLast ? '0 0 auto' : '1 1 0' }}>
            {/* Step circle */}
            <div
              className="flex items-center justify-center rounded-full shrink-0"
              style={{
                width: 28,
                height: 28,
                fontSize: 11,
                fontWeight: 600,
                background: isCompleted || isActive ? '#C2541A' : 'rgba(255,255,255,0.06)',
                color: isCompleted || isActive ? '#fff' : 'rgba(255,255,255,0.3)',
                boxShadow: isActive ? '0 0 0 3px rgba(194,84,26,0.2)' : 'none',
              }}
            >
              {isCompleted ? <Check className="w-3.5 h-3.5" /> : stepNumber}
            </div>

            {/* Connector line */}
            {!isLast && (
              <div
                className="flex-1 mx-1.5"
                style={{
                  height: 1,
                  background: isCompleted ? '#C2541A' : 'rgba(255,255,255,0.08)',
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

export function OnboardingShell({
  children,
  step,
  totalSteps,
  stepLabel,
  onBack,
  maxWidth,
}: OnboardingShellProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(t);
  }, []);

  return (
    <div
      data-theme="dark"
      className="min-h-screen flex items-center justify-center p-6"
      style={{ background: 'rgb(10,13,20)' }}
    >
      <div
        className="w-full relative"
        style={{
          maxWidth: maxWidth || '480px',
          opacity: visible ? 1 : 0,
          transition: 'opacity 200ms ease',
        }}
      >
        {/* Back button — top-left outside card flow */}
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="absolute -top-10 left-0 flex items-center justify-center transition-colors"
            style={{
              width: 28,
              height: 28,
              borderRadius: 6,
              border: '1px solid rgba(255,255,255,0.08)',
              color: 'rgba(255,255,255,0.35)',
            }}
          >
            <ArrowLeft className="w-3.5 h-3.5" />
          </button>
        )}

        {/* Card */}
        <div
          style={{
            background: 'rgba(255,255,255,0.02)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 12,
            overflow: 'hidden',
          }}
        >
          <div className="p-8">
            {/* Progress indicator */}
            <StepProgressIndicator currentStep={step} totalSteps={totalSteps} />

            {/* Wordmark */}
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: '#C2541A',
                letterSpacing: '0.02em',
                marginBottom: 32,
              }}
            >
              {SITE_NAME}
            </div>

            {/* Content */}
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
